import { defineBoot } from '@quasar/app-vite/wrappers';
import _isEqual from 'lodash/isEqual';
import { Platform } from 'quasar';
import chain from 'src/services/chain';

/**
 * @param {{ app: import('vue').App }} payload
 */
export default defineBoot(({ app }) => {
  app.directive('capacitor-native-select', {
    bind(el, binding, vnode) {
      const { componentInstance } = vnode;

      if (!Platform.is.capacitor) {
        return;
      }

      if (componentInstance.$options.propsData.useInput !== undefined) {
        console.warn('The capacitor-native-select directive cannot be used with "use-input".');
        return;
      }

      if (componentInstance.$options.propsData.multiple !== undefined) {
        console.warn('The capacitor-native-select directive cannot be used with "multiple".');
        return;
      }

      const selectEl = document.createElement('select');

      selectEl.addEventListener('change', () => {
        const { propsData } = componentInstance.$options;

        const option = propsData.options[selectEl.selectedIndex];

        // Get value from option-value definition or the "value" prop
        const optionValue = propsData.optionValue ? option[propsData.optionValue] : option.value;

        // Use the option itself if no option value exists (i.e. the option is the value)
        const value = optionValue !== undefined ? optionValue : option;

        componentInstance.$emit('update:model-value', propsData.emitValue !== undefined ? value : option);
      });

      selectEl.style = 'position: absolute; top: 0; left: 0; width: 100%; opacity: 0;';

      componentInstance.$attrs.class = 'relative-position';
      componentInstance.$refs.nativeSelect = selectEl;

      binding.def.updateDisabledState(vnode);

      binding.def.updateOptions(el, binding, vnode);

      binding.def.updateSelectSize(vnode, el.offsetWidth, el.offsetHeight);
      componentInstance.$data.selectResizeObserver = new ResizeObserver((entries) => {
        const { contentRect } = entries[0];
        binding.def.updateSelectSize(vnode, contentRect.width, contentRect.height);
      });
      componentInstance.$data.selectResizeObserver.observe(el);
    },
    componentUpdated(el, binding, vnode) {
      const { componentInstance } = vnode;
      const { propsData } = componentInstance.$options;
      const { nativeSelect } = componentInstance.$refs;

      if (!nativeSelect) {
        return;
      }

      binding.def.updateDisabledState(vnode);

      if (
        !componentInstance.nativeOptions.length ||
        !binding.def.isArrayEqual(componentInstance.nativeOptions, propsData.options)
      ) {
        binding.def.updateOptions(el, binding, vnode);
      }
    },
    detach(vnode) {
      const { nativeSelect } = vnode.componentInstance.$refs || {};
      if (nativeSelect?.parentElement) {
        nativeSelect.parentElement.removeChild(nativeSelect);
      }
    },
    getOptionValue(option, componentInstance) {
      const { propsData } = componentInstance.$options;
      const htmlOptionValueKey = componentInstance.$attrs['html-option-value-key'];

      if (option.value && htmlOptionValueKey) {
        return option.value[htmlOptionValueKey];
      }

      return propsData.optionValue ? option[propsData.optionValue] : option.value || option;
    },
    isArrayEqual(x, y) {
      return chain(x).differenceWith(y, _isEqual).isEmpty().value();
    },
    unbind(el, _, vnode) {
      vnode.componentInstance.$data.selectResizeObserver?.unobserve(el);
    },
    updateDisabledState(vnode) {
      const { componentInstance } = vnode;
      const { propsData } = componentInstance.$options;
      vnode.componentInstance.$refs.nativeSelect.disabled = Boolean(
        propsData.readonly || propsData.disable || propsData.loading
      );
    },
    async updateOptions(el, binding, vnode) {
      const { componentInstance } = vnode;
      const { propsData } = componentInstance.$options;
      const { nativeSelect } = componentInstance.$refs;

      binding.def.detach(vnode);

      // Note: no event handlers are added to child nodes, this is fine
      nativeSelect.innerHTML = '';

      // Save options for change comparison later
      componentInstance.nativeOptions = [...propsData.options];

      let selectedIndex = 0;
      let selectedValue = '';
      propsData.options.forEach((option, index) => {
        const optionEl = document.createElement('option');
        optionEl.innerText = propsData.optionLabel ? option[propsData.optionLabel] : option.label || option;

        const optionValue = binding.def.getOptionValue(option, componentInstance);

        optionEl.value = typeof optionValue === 'object' ? index : optionValue;
        if (
          (propsData.mapOptions !== undefined && componentInstance.value === optionValue) ||
          (propsData.mapOptions === undefined && componentInstance.value === option)
        ) {
          selectedIndex = index;
          selectedValue = optionEl.value;
        }
        nativeSelect.appendChild(optionEl);
      });

      el.querySelector('.q-field__inner').appendChild(nativeSelect);

      await componentInstance.$nextTick();

      nativeSelect.selectedIndex = selectedIndex;
      nativeSelect.value = selectedValue;
    },
    updateSelectSize(vnode, width, height) {
      vnode.componentInstance.$refs.nativeSelect.style.height = `${height}px`;
      vnode.componentInstance.$refs.nativeSelect.style.width = `${width}px`;
    },
  });
});
